import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  InputNumber,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  Upload,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { RcFile } from "antd/es/upload";
import { InboxOutlined } from "@ant-design/icons";

import {
  ApiError,
  type ColumnMapping,
  type CommitResponse,
  type DryRunResponse,
  type InspectResponse,
  api,
} from "../api/client";

const { Title, Paragraph, Text } = Typography;

// Used as the base when partially updating the mapping from a setState
// callback, so we never construct an object missing the required string
// fields and lose TypeScript's narrowing.
const emptyMapping: ColumnMapping = {
  email: "",
  nickname: "",
  real_name: [],
  filter_column: null,
  arrival_date_column: null,
  departure_date_column: null,
  submission_timestamp_column: null,
  event_year: null,
};

function actionColor(action: string): string {
  switch (action) {
    case "create":
      return "green";
    case "adopt":
      return "blue";
    case "update":
      return "geekblue";
    case "unchanged":
      return "default";
    case "filtered":
      return "default";
    case "conflict":
      return "volcano";
    case "skip":
      return "default";
    default:
      return "default";
  }
}

// A "padding skip" is a SKIP row whose only parser errors are
// ``missing_email`` and/or ``missing_nickname`` — typically the empty
// trailing rows spreadsheets pad below the last person record. These
// don't represent operator action items and would just clutter the top
// of the dry-run preview, so we push them to the end of the table.
const _PADDING_SKIP_ERRORS = new Set(["missing_email", "missing_nickname"]);
function isPaddingSkip(row: DryRunResponse["rows"][number]): boolean {
  if (row.action !== "skip") return false;
  const errs = row.parser_errors ?? [];
  if (errs.length === 0) return false;
  return errs.every((e) => _PADDING_SKIP_ERRORS.has(e));
}

// Wrap a cell's value in a strong-tint badge when this field is in the
// row's ``changed_fields`` list (UPDATE rows only). The pale row-tint
// already says "this row will be patched"; this overlay tells the
// operator exactly which cells the patch is touching at a glance. Has
// no effect for non-UPDATE rows or fields that didn't change.
function renderMaybeChanged(
  display: React.ReactNode,
  row: DryRunResponse["rows"][number],
  field: string,
): React.ReactNode {
  const changed = row.changed_fields?.includes(field) ?? false;
  if (!changed) return display;
  return (
    <span
      style={{
        background: "rgba(47, 84, 235, 0.20)",
        padding: "1px 6px",
        borderRadius: 4,
        fontWeight: 600,
      }}
      title="will be updated on commit"
    >
      {display}
    </span>
  );
}

// Pale-tint backgrounds for dry-run rows. The Action column already
// shows the categorical tag; this paints the WHOLE row in a matching
// hue so the operator can scan the table by colour ("am I about to
// write or skip this person?") without reading each row's tag. Tints
// are kept very light so text remains legible against AntD's default
// foreground colour.
function actionRowTint(action: string): string | undefined {
  switch (action) {
    case "create":
      return "rgba(82, 196, 26, 0.10)"; // green-6 @ 10%
    case "adopt":
      return "rgba(22, 119, 255, 0.10)"; // blue-6 @ 10%
    case "update":
      return "rgba(47, 84, 235, 0.10)"; // geekblue-6 @ 10%
    case "conflict":
      return "rgba(250, 84, 28, 0.14)"; // volcano-6 @ 14%
    case "skip":
      return "rgba(255, 77, 79, 0.14)"; // red-5 @ 14%
    case "filtered":
      return "rgba(0, 0, 0, 0.04)"; // muted grey
    default:
      return undefined; // unchanged / unknown: no tint
  }
}

// AntD's Select needs single-line option labels — multi-line headers from
// Google Forms exports render with awkward line breaks otherwise. We keep
// the original string as the value (the server matches it verbatim) but
// flatten the label for display.
function flattenHeader(h: string, max = 80): string {
  const collapsed = h.replace(/\s+/g, " ").trim();
  return collapsed.length > max ? `${collapsed.slice(0, max - 1)}…` : collapsed;
}

export const ImportPage = () => {
  const [csv, setCsv] = useState<File | null>(null);
  const [inspection, setInspection] = useState<InspectResponse | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping | null>(null);
  const [dryRun, setDryRun] = useState<DryRunResponse | null>(null);
  const [commit, setCommit] = useState<CommitResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Default-hide the high-volume / low-signal rows so the operator's eye
  // lands on rows that actually need a decision (skip, conflict, create,
  // adopt, update). Switches below let them be brought back in.
  const [showFiltered, setShowFiltered] = useState(false);
  const [showUnchanged, setShowUnchanged] = useState(false);
  // Festival arrival/departure dates default to ON — the festival
  // roster IS the primary use case and operators were having to flip
  // the switch on every import. The toggle stays in the UI so a
  // non-roster import can opt OUT, and ``submittedMapping`` still
  // strips the columns from the payload whenever the switch is off.
  const [enableArrivalDeparture, setEnableArrivalDeparture] = useState(true);

  const reset = () => {
    setCsv(null);
    setInspection(null);
    setMapping(null);
    setDryRun(null);
    setCommit(null);
    setError(null);
    setEnableArrivalDeparture(true);
  };

  // Apply the festival-roster toggle: when off, force arrival/departure
  // sources (manual columns AND auto-detected timetable grid) to empty
  // regardless of what the inspector suggested or the operator last
  // picked. Used at dry-run + commit submit time so the server sees
  // exactly what the operator confirmed.
  const submittedMapping = (m: ColumnMapping): ColumnMapping =>
    enableArrivalDeparture
      ? m
      : {
          ...m,
          arrival_date_column: null,
          departure_date_column: null,
          timetable_grid_columns: [],
        };

  const onUpload = async (file: RcFile) => {
    setError(null);
    setInspection(null);
    setMapping(null);
    setDryRun(null);
    setCommit(null);
    setCsv(file);
    setBusy(true);
    try {
      const r = await api.importInspect(file);
      setInspection(r);
      if (r.suggested_mapping) setMapping(r.suggested_mapping);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
    return false;
  };

  const onRunDryRun = async () => {
    if (!csv || !mapping) return;
    setError(null);
    setBusy(true);
    try {
      const r = await api.importDryRun(csv, submittedMapping(mapping));
      setDryRun(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onCommit = async () => {
    if (!csv || !mapping) return;
    setError(null);
    setBusy(true);
    try {
      const r = await api.importCommit(csv, { mapping: submittedMapping(mapping) });
      setCommit(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const headerOptions = useMemo(
    () =>
      (inspection?.headers ?? []).map((h) => ({
        value: h,
        label: flattenHeader(h),
        title: h, // full text on hover for the long bilingual ones
      })),
    [inspection],
  );

  const mappingComplete = Boolean(mapping?.email && mapping?.nickname);

  // Shape the dry-run rows for display: hide low-signal buckets behind
  // the switches, and float skipped rows to the top because they
  // typically carry the parser-error reason the operator most needs to
  // see (and act on) before committing.
  const visibleRows = useMemo(() => {
    if (!dryRun) return [];
    const filtered = dryRun.rows.filter((r) => {
      // Warning-bearing rows ALWAYS stay visible. On re-import of the
      // same CSV they diagnose as UNCHANGED (the stripped-null dates
      // match the prior null commit), which would otherwise hide them
      // behind the "Show unchanged" toggle — silently dropping the
      // warning the operator must still act on.
      if ((r.parser_warnings?.length ?? 0) > 0) return true;
      if (r.action === "filtered" && !showFiltered) return false;
      if (r.action === "unchanged" && !showUnchanged) return false;
      return true;
    });
    return [...filtered].sort((a, b) => {
      // Tiered ordering:
      //   0 — SKIPs with REAL errors (top, demand operator action)
      //   1 — rows carrying a non-blocking warning (date strip etc.)
      //   2 — normal commit-able rows (create/adopt/update/unchanged)
      //   3 — padding-row SKIPs (bottom — empty trailing CSV rows
      //       whose only errors are ``missing_email`` /
      //       ``missing_nickname``, typically spreadsheet padding
      //       below the last person row)
      const pin = (row: DryRunResponse["rows"][number]): number => {
        if (row.action === "skip") return isPaddingSkip(row) ? 3 : 0;
        if ((row.parser_warnings?.length ?? 0) > 0) return 1;
        return 2;
      };
      const aPin = pin(a);
      const bPin = pin(b);
      if (aPin !== bPin) return aPin - bPin;
      // Stable secondary order: preserve CSV order.
      return a.line_number - b.line_number;
    });
  }, [dryRun, showFiltered, showUnchanged]);

  const counts = dryRun?.summary ?? {};
  const filteredCount = counts.filtered ?? 0;
  const unchangedCount = counts.unchanged ?? 0;

  // Arrival/departure columns only appear when at least one mapped row
  // actually carried a value — keeps the dry-run table compact for
  // imports that don't use the festival roster columns.
  const hasArrivalCol = useMemo(
    () => (dryRun?.rows ?? []).some((r) => r.arrival_date),
    [dryRun],
  );
  const hasDepartureCol = useMemo(
    () => (dryRun?.rows ?? []).some((r) => r.departure_date),
    [dryRun],
  );
  // Volunteer-days column only appears when at least one row carried V cells.
  const hasVolunteerCol = useMemo(
    () => (dryRun?.rows ?? []).some((r) => (r.volunteer_dates?.length ?? 0) > 0),
    [dryRun],
  );

  const columns: ColumnsType<DryRunResponse["rows"][number]> = [
    { title: "Line", dataIndex: "line_number", width: 60 },
    {
      title: "Action",
      dataIndex: "action",
      render: (a: string) => <Tag color={actionColor(a)}>{a}</Tag>,
    },
    { title: "Email", dataIndex: "email" },
    { title: "Nickname", dataIndex: "nickname" },
    {
      title: "Real name",
      dataIndex: "real_name",
      render: (v: string | null, row) => renderMaybeChanged(v ?? "—", row, "real_name"),
    },
    ...(hasArrivalCol
      ? [
          {
            title: "Arrival",
            dataIndex: "arrival_date",
            render: (v: string | null, row: DryRunResponse["rows"][number]) =>
              renderMaybeChanged(v ?? "—", row, "arrival_date"),
          },
        ]
      : []),
    ...(hasDepartureCol
      ? [
          {
            title: "Departure",
            dataIndex: "departure_date",
            render: (v: string | null, row: DryRunResponse["rows"][number]) =>
              renderMaybeChanged(v ?? "—", row, "departure_date"),
          },
        ]
      : []),
    ...(hasVolunteerCol
      ? [
          {
            title: "Volunteer days",
            dataIndex: "volunteer_dates",
            render: (v: string[] | undefined) => {
              const days = v ?? [];
              if (days.length === 0) return "—";
              return (
                <span title={days.join(", ")}>
                  <Tag color="purple">{days.length}</Tag>
                </span>
              );
            },
          },
        ]
      : []),
    {
      title: "Notes",
      render: (_: unknown, row) => {
        const warns = row.parser_warnings ?? [];
        const errs = row.parser_errors.join(", ");
        const notes = (row.parser_notes ?? []).join(", ");
        const detail = row.detail ?? "";
        const text = [errs, notes, detail].filter(Boolean).join("; ");
        if (warns.length === 0) return text || "—";
        return (
          <Space size={4} direction="vertical" style={{ width: "100%" }}>
            <Space size={4} wrap>
              {warns.map((w) => (
                <Tag color="warning" key={w}>{w}</Tag>
              ))}
            </Space>
            {text ? <span>{text}</span> : null}
          </Space>
        );
      },
    },
  ];

  return (
    <Card>
      <Title level={2}>CSV import</Title>
      <Paragraph type="secondary">
        Upload a CSV, map its columns to <strong>email</strong>, <strong>nickname</strong> and
        (optionally) <strong>real name</strong>, then preview the dry-run and commit. The
        importer guesses a mapping from the headers — confirm or override before running the
        dry-run.
      </Paragraph>
      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} showIcon />}

      {!inspection && (
        <Upload.Dragger
          accept=".csv,text/csv"
          beforeUpload={(file) => onUpload(file as RcFile)}
          showUploadList={false}
          disabled={busy}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag a CSV here to start.</p>
        </Upload.Dragger>
      )}

      {inspection && !dryRun && (
        <div style={{ marginTop: 16 }}>
          <Space style={{ marginBottom: 16 }}>
            <Text strong>{csv?.name}</Text>
            <Text type="secondary">{inspection.headers.length} columns detected</Text>
            <Button size="small" onClick={reset} disabled={busy}>
              Pick a different file
            </Button>
          </Space>
          <Alert
            style={{ marginBottom: 16 }}
            type={enableArrivalDeparture ? "info" : undefined}
            showIcon={enableArrivalDeparture}
            message={
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Space>
                  <Switch
                    checked={enableArrivalDeparture}
                    onChange={setEnableArrivalDeparture}
                  />
                  <Text strong>Import festival arrival / departure dates</Text>
                  <Text type="secondary">
                    on by default; flip off for non-roster imports
                  </Text>
                </Space>
                {(mapping?.timetable_grid_columns?.length ?? 0) > 0 && (
                  <Text type="secondary" style={{ marginLeft: 46 }}>
                    Timetable grid auto-detected:{" "}
                    {mapping?.timetable_grid_columns?.length} day cells (
                    {flattenHeader(mapping?.timetable_grid_columns?.[0] ?? "", 24)}{" "}
                    →{" "}
                    {flattenHeader(
                      mapping?.timetable_grid_columns?.[
                        (mapping?.timetable_grid_columns?.length ?? 1) - 1
                      ] ?? "",
                      24,
                    )}
                    ){" "}
                    {enableArrivalDeparture
                      ? "— first/last marked day per row becomes arrival/departure."
                      : "— turn the toggle on to use it."}
                  </Text>
                )}
                {(mapping?.timetable_grid_columns?.length ?? 0) > 0 && (
                  <Space style={{ marginLeft: 46 }} align="center" wrap>
                    <Text type="secondary">Event year</Text>
                    <InputNumber
                      min={2000}
                      max={2100}
                      value={mapping?.event_year ?? undefined}
                      onChange={(v) =>
                        setMapping((m) => ({
                          ...emptyMapping,
                          ...m,
                          event_year: typeof v === "number" ? v : null,
                        }))
                      }
                    />
                    <Text type="secondary">
                      anchors the grid&apos;s V-days + arrival/departure (the
                      headers carry no year)
                    </Text>
                  </Space>
                )}
              </Space>
            }
          />
          <Form
            layout="vertical"
            // The Select widgets stretch to the full column width; without
            // labelCol/wrapperCol antd defaults are fine here.
          >
            <Form.Item
              label="Email column"
              required
              extra={inspection.rationale.email}
              validateStatus={mapping?.email ? "success" : "warning"}
            >
              <Select
                value={mapping?.email}
                onChange={(v: string) => setMapping((m) => ({ ...emptyMapping, ...m, email: v }))}
                options={headerOptions}
                placeholder="pick the column that holds the email address"
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>

            <Form.Item
              label="Nickname column"
              required
              extra={inspection.rationale.nickname}
              validateStatus={mapping?.nickname ? "success" : "warning"}
            >
              <Select
                value={mapping?.nickname}
                onChange={(v: string) =>
                  setMapping((m) => ({ ...emptyMapping, ...m, nickname: v }))
                }
                options={headerOptions}
                placeholder="pick the column that holds the nickname"
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>

            <Form.Item
              label="Real name column(s)"
              extra={
                inspection.rationale.real_name ??
                "Optional. Pick one column, or multiple (e.g. first + last name) — they're joined with a space."
              }
            >
              <Select
                mode="multiple"
                allowClear
                value={mapping?.real_name ?? []}
                onChange={(v: string[]) =>
                  setMapping((m) => ({ ...emptyMapping, ...m, real_name: v }))
                }
                options={headerOptions}
                placeholder="optional — leave empty to skip"
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>

            <Form.Item
              label="Only import rows where column is truthy"
              extra={
                inspection.rationale.filter_column ??
                "Optional. Pick a yes/no column (e.g. 'ready to be confirmed'); rows whose value isn't TRUE / yes / 1 are skipped."
              }
            >
              <Select
                allowClear
                value={mapping?.filter_column ?? undefined}
                onChange={(v: string | undefined) =>
                  setMapping((m) => ({
                    ...emptyMapping,
                    ...m,
                    filter_column: v ?? null,
                  }))
                }
                options={headerOptions}
                placeholder="optional — leave empty to import every row"
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>

            {enableArrivalDeparture && (
              <>
                <Form.Item
                  label="Arrival date column"
                  extra={
                    inspection.rationale.arrival_date_column ??
                    "Optional. Pick the column that holds the festival arrival date (DD.MM.YYYY, YYYY-MM-DD, or DD/MM/YYYY). Blank cells don't clear existing values."
                  }
                >
                  <Select
                    allowClear
                    value={mapping?.arrival_date_column ?? undefined}
                    onChange={(v: string | undefined) =>
                      setMapping((m) => ({
                        ...emptyMapping,
                        ...m,
                        arrival_date_column: v ?? null,
                      }))
                    }
                    options={headerOptions}
                    placeholder="optional — leave empty if the CSV has no arrival date"
                    showSearch
                    optionFilterProp="label"
                  />
                </Form.Item>

                <Form.Item
                  label="Departure date column"
                  extra={
                    inspection.rationale.departure_date_column ??
                    "Optional. Pick the column that holds the festival departure date (same formats as arrival)."
                  }
                >
                  <Select
                    allowClear
                    value={mapping?.departure_date_column ?? undefined}
                    onChange={(v: string | undefined) =>
                      setMapping((m) => ({
                        ...emptyMapping,
                        ...m,
                        departure_date_column: v ?? null,
                      }))
                    }
                    options={headerOptions}
                    placeholder="optional — leave empty if the CSV has no departure date"
                    showSearch
                    optionFilterProp="label"
                  />
                </Form.Item>
              </>
            )}
          </Form>

          {inspection.preview.length > 0 && (
            <Card size="small" title="Preview" style={{ marginTop: 8 }}>
              <Descriptions size="small" column={1} colon={false}>
                {(["email", "nickname", "real_name"] as const).map((field) => {
                  const cols =
                    field === "real_name"
                      ? mapping?.real_name ?? []
                      : mapping?.[field]
                        ? [mapping[field] as string]
                        : [];
                  if (cols.length === 0) {
                    return (
                      <Descriptions.Item key={field} label={field}>
                        <Text type="secondary">—</Text>
                      </Descriptions.Item>
                    );
                  }
                  const samples = inspection.preview.map((row) => {
                    const values = cols.map((col) => {
                      const idx = inspection.headers.indexOf(col);
                      return idx >= 0 ? row[idx] ?? "" : "";
                    });
                    return values.filter(Boolean).join(" ");
                  });
                  return (
                    <Descriptions.Item key={field} label={field}>
                      <Text code>{samples.join("  •  ") || "—"}</Text>
                    </Descriptions.Item>
                  );
                })}
              </Descriptions>
            </Card>
          )}

          <Space style={{ marginTop: 16 }}>
            <Button
              type="primary"
              onClick={onRunDryRun}
              disabled={!mappingComplete}
              loading={busy}
            >
              Run dry-run
            </Button>
          </Space>
        </div>
      )}

      {dryRun && !commit && (
        <div style={{ marginTop: 24 }}>
          <Descriptions title="Dry-run summary" bordered size="small" column={4}>
            {Object.entries(dryRun.summary).map(([k, v]) => (
              <Descriptions.Item key={k} label={k}>
                {v}
              </Descriptions.Item>
            ))}
          </Descriptions>
          <Space style={{ marginTop: 16 }} size="large" wrap>
            <Space size="small">
              <Switch
                checked={showFiltered}
                onChange={setShowFiltered}
                disabled={filteredCount === 0}
                size="small"
              />
              <Text>Show filtered rows ({filteredCount})</Text>
            </Space>
            <Space size="small">
              <Switch
                checked={showUnchanged}
                onChange={setShowUnchanged}
                disabled={unchangedCount === 0}
                size="small"
              />
              <Text>Show unchanged rows ({unchangedCount})</Text>
            </Space>
          </Space>
          <Table
            columns={columns}
            dataSource={visibleRows}
            rowKey="line_number"
            style={{ marginTop: 16 }}
            pagination={false}
            onRow={(row) => {
              const tint = actionRowTint(row.action);
              return tint ? { style: { background: tint } } : {};
            }}
          />
          <Space style={{ marginTop: 16 }}>
            <Button type="primary" onClick={onCommit} loading={busy}>
              Commit import
            </Button>
            <Button onClick={() => setDryRun(null)} disabled={busy}>
              Back to mapping
            </Button>
            <Button onClick={reset} disabled={busy}>
              Start over
            </Button>
          </Space>
        </div>
      )}

      {commit && (
        <div style={{ marginTop: 24 }}>
          <Alert
            type={commit.summary.failed === 0 ? "success" : "warning"}
            message={`Import done: ${commit.summary.success ?? 0} succeeded, ${
              commit.summary.failed ?? 0
            } failed${
              commit.summary.volunteer_days_added
                ? `, ${commit.summary.volunteer_days_added} volunteer day(s) added`
                : ""
            }`}
            showIcon
          />
          <Table
            columns={[
              { title: "Line", dataIndex: "line_number", width: 60 },
              {
                title: "Action",
                dataIndex: "action",
                render: (a: string) => <Tag color={actionColor(a)}>{a}</Tag>,
              },
              {
                title: "Result",
                dataIndex: "success",
                render: (v: boolean) =>
                  v ? <Tag color="green">ok</Tag> : <Tag color="red">failed</Tag>,
              },
              { title: "Email", dataIndex: "email" },
              { title: "Nickname", dataIndex: "nickname" },
              {
                title: "Real name",
                dataIndex: "real_name",
                render: (v: string | null) => v ?? "—",
              },
              { title: "Detail", dataIndex: "error", render: (v: string | null) => v ?? "—" },
            ]}
            dataSource={commit.results}
            rowKey="line_number"
            style={{ marginTop: 16 }}
            pagination={false}
          />
          <Space style={{ marginTop: 16 }}>
            <Button onClick={reset}>Import another file</Button>
          </Space>
        </div>
      )}
    </Card>
  );
};
